import React, { useCallback } from 'react';
import { Button, ButtonProps } from '@mui/material';
import SmsIcon from '@mui/icons-material/Sms';
import { useTranslate } from 'hooks/use-translate';
import { useIsBelowMd } from 'hooks/use-is-below-md';
import { useAppDispatch } from 'store';
import { patientSmsMessagesActions } from 'store/patient-sms-messages';
import { APP_FORMAT_DATE, APP_FORMAT_TIME } from 'configs/const';
import { dateFormat } from 'utils/dates';

interface Props extends ButtonProps {
  userPatientProfileID: string;
  toPhoneNumber: string | undefined | null;
  patient: Pick<Components.Schemas.UserPatientProfile, 'firstName' | 'lastName'>;
  meeting: {
    meetingFromDateTime: string;
    meetingToDateTime: string;
  } | null;
  onDone?: (data: { message?: string | null }) => void;
}

export const ButtonSendSms: React.FC<React.PropsWithChildren<Props>> = ({
  userPatientProfileID,
  toPhoneNumber,
  onDone,
  patient,
  meeting,
  ...rest
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslate();
  // Phase F3 (F3-03, D-69): below md the card's action bar has no room for labels; the icon keeps the title (F1's add-task pattern).
  const isBelowMd = useIsBelowMd();
  const onClick = useCallback(() => {
    const fromDate = meeting?.meetingFromDateTime;
    const toDate = meeting?.meetingToDateTime;

    const templatePayload = {
      meetingFromDate: fromDate ? dateFormat(fromDate, APP_FORMAT_DATE) : '',
      meetingFromTime: fromDate ? dateFormat(fromDate, APP_FORMAT_TIME) : '',

      meetingToDate: toDate ? dateFormat(toDate, APP_FORMAT_DATE) : '',
      meetingToTime: toDate ? dateFormat(toDate, APP_FORMAT_TIME) : '',

      patient,
    };
    dispatch(
      patientSmsMessagesActions.add({
        uid: `patient-card__${userPatientProfileID}`,
        formData: { userPatientProfileID, toPhoneNumber },
        templatePayload,
        onSuccess: onDone,
      }),
    );
  }, [dispatch, userPatientProfileID, toPhoneNumber, patient, onDone, meeting]);

  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={isBelowMd ? undefined : <SmsIcon />}
      title={t('send-sms')}
      aria-label={t('send-sms')}
      {...rest}
      onClick={onClick}
      disabled={!toPhoneNumber || rest.disabled}
    >
      {isBelowMd ? <SmsIcon /> : t('send-sms')}
    </Button>
  );
};
